import React, { useState, useEffect } from 'react';
import { Form, Upload, Button, Table, Typography, Alert, Row, Col, Select, Switch, Divider, Spin } from 'antd';
import { InboxOutlined, FileTextOutlined, DownloadOutlined } from '@ant-design/icons';

const { Dragger } = Upload;
const { Text, Title } = Typography;
const { Option } = Select;

const CsvInputNodeProperties = ({ config, form }) => {
  const [fileData, setFileData] = useState(config.fileData || null);
  const [schema, setSchema] = useState(config.schema || []);
  const [previewData, setPreviewData] = useState(config.previewData || []);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState(config.fileName || '');

  // Update form when data changes
  useEffect(() => {
    form.setFieldsValue({
      fileData: fileData,
      schema: schema,
      previewData: previewData,
      fileName: fileName
    });
  }, [fileData, schema, previewData, fileName, form]);

  // Detect data type based on value
  const detectDataType = (value) => {
    if (value === '' || value === null || value === undefined) {
      return 'string';
    }
    
    const strValue = String(value).trim();
    
    // Check for number
    if (!isNaN(strValue) && !isNaN(parseFloat(strValue))) {
      return strValue.includes('.') ? 'float' : 'integer';
    }
    
    // Check for boolean
    if (['true', 'false', 'TRUE', 'FALSE', '1', '0'].includes(strValue)) {
      return 'boolean';
    }
    
    // Check for date (basic patterns)
    const datePatterns = [
      /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
      /^\d{2}\/\d{2}\/\d{4}$/, // MM/DD/YYYY
      /^\d{2}-\d{2}-\d{4}$/, // DD-MM-YYYY
    ];
    
    if (datePatterns.some(pattern => pattern.test(strValue))) {
      return 'date';
    }
    
    return 'string';
  };

  // Parse CSV content
  const parseCSV = (text) => {
    const lines = text.split('\n').filter(line => line.trim() !== '');
    if (lines.length < 1) return { headers: [], rows: [] };
    
    // Parse headers
    const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
    
    // Parse data rows (take first 3 rows for analysis)
    const rows = [];
    for (let i = 1; i < Math.min(4, lines.length); i++) {
      const values = lines[i].split(',').map(value => value.trim().replace(/"/g, ''));
      rows.push(values);
    }
    
    return { headers, rows };
  };

  // Analyze schema from parsed data
  const analyzeSchema = (headers, rows) => {
    const schemaAnalysis = headers.map((header, index) => {
      const columnValues = rows.map(row => row[index]).filter(val => val !== undefined);
      
      // Detect most common type
      const types = columnValues.map(detectDataType);
      const typeCount = types.reduce((acc, type) => {
        acc[type] = (acc[type] || 0) + 1;
        return acc;
      }, {});
      
      const mostCommonType = Object.keys(typeCount).reduce((a, b) => 
        typeCount[a] > typeCount[b] ? a : b, 'string'
      );

      return {
        key: index,
        columnName: header,
        dataType: mostCommonType,
        sampleValues: columnValues.slice(0, 3),
        nullable: columnValues.some(val => val === '' || val === null),
        unique: new Set(columnValues).size === columnValues.length
      };
    });

    return schemaAnalysis;
  };

  // Handle file upload
  const handleFileUpload = {
    name: 'csvFile',
    multiple: false,
    accept: '.csv',
    beforeUpload: (file) => {
      if (!file.name.toLowerCase().endsWith('.csv')) {
        return false;
      }
      return false; // Prevent auto upload
    },
    onChange: (info) => {
      const { file } = info;
      if (file.status !== 'uploading') {
        setLoading(true);
        setFileName(file.name);
        
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const text = e.target.result;
            const { headers, rows } = parseCSV(text);
            
            if (headers.length === 0) {
              throw new Error('No valid CSV headers found');
            }

            // Analyze schema
            const detectedSchema = analyzeSchema(headers, rows);
            
            // Prepare preview data for table
            const tableData = rows.map((row, index) => {
              const rowData = { key: index };
              headers.forEach((header, colIndex) => {
                rowData[header] = row[colIndex] || '';
              });
              return rowData;
            });

            setFileData(text);
            setSchema(detectedSchema);
            setPreviewData(tableData);
            setLoading(false);
          } catch (error) {
            console.error('Error parsing CSV:', error);
            setLoading(false);
          }
        };
        reader.readAsText(file);
      }
    }
  };

  // Table columns for schema display
  const schemaColumns = [
    {
      title: 'Column Name',
      dataIndex: 'columnName',
      key: 'columnName',
      width: 150,
    },
    {
      title: 'Data Type',
      dataIndex: 'dataType',
      key: 'dataType',
      width: 120,
      render: (type, record) => (
        <Select 
          value={type} 
          size="small" 
          style={{ width: '100%' }}
          onChange={(value) => {
            const newSchema = [...schema];
            newSchema[record.key].dataType = value;
            setSchema(newSchema);
          }}
        >
          <Option value="string">String</Option>
          <Option value="integer">Integer</Option>
          <Option value="float">Float</Option>
          <Option value="boolean">Boolean</Option>
          <Option value="date">Date</Option>
        </Select>
      )
    },
    {
      title: 'Sample Values',
      dataIndex: 'sampleValues',
      key: 'sampleValues',
      render: (values) => (
        <Text style={{ fontSize: '11px' }}>
          {values.slice(0, 2).join(', ')}
        </Text>
      )
    },
    {
      title: 'Nullable',
      dataIndex: 'nullable',
      key: 'nullable',
      width: 80,
      render: (nullable) => (
        <Text style={{ fontSize: '11px' }}>
          {nullable ? 'Yes' : 'No'}
        </Text>
      )
    }
  ];

  // Table columns for data preview
  const previewColumns = schema.map(col => ({
    title: col.columnName,
    dataIndex: col.columnName,
    key: col.columnName,
    width: 120,
    ellipsis: true,
    render: (text) => (
      <Text style={{ fontSize: '11px' }}>
        {String(text || '').length > 20 ? String(text).substring(0, 20) + '...' : text}
      </Text>
    )
  }));

  return (
    <div>
      <Form.Item label="CSV File Upload" name="fileName" initialValue={fileName}>
        <div>
          <Dragger {...handleFileUpload} showUploadList={false}>
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click or drag CSV file to upload</p>
            <p className="ant-upload-hint">
              Supports CSV files only. First row should contain column headers.
            </p>
          </Dragger>
          
          {fileName && (
            <Alert 
              message={`File loaded: ${fileName}`}
              type="success" 
              showIcon 
              style={{ marginTop: 8 }}
            />
          )}
        </div>
      </Form.Item>

      {/* Hidden form fields to store data in form */}
      <Form.Item name="fileData" initialValue={fileData} style={{ display: 'none' }}>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="schema" initialValue={schema} style={{ display: 'none' }}>
        <input type="hidden" />
      </Form.Item>
      <Form.Item name="previewData" initialValue={previewData} style={{ display: 'none' }}>
        <input type="hidden" />
      </Form.Item>

      {loading && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: 8 }}>Analyzing CSV file...</Text>
        </div>
      )}

      {schema.length > 0 && !loading && (
        <>
          <Divider orientation="left" style={{ fontSize: '12px' }}>Data Schema Analysis</Divider>
          
          <Alert 
            message={`Detected ${schema.length} columns from ${fileName}`}
            type="info" 
            showIcon 
            style={{ marginBottom: 12 }}
          />

          <Table
            columns={schemaColumns}
            dataSource={schema}
            size="small"
            pagination={false}
            scroll={{ x: 500, y: 200 }}
            style={{ marginBottom: 16 }}
          />

          <Divider orientation="left" style={{ fontSize: '12px' }}>Data Preview (First 3 Rows)</Divider>
          
          <Table
            columns={previewColumns}
            dataSource={previewData}
            size="small"
            pagination={false}
            scroll={{ x: 600, y: 150 }}
            style={{ marginBottom: 16 }}
          />

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item label="Delimiter" name="delimiter" initialValue={config.delimiter || ','}>
                <Select>
                  <Option value=",">Comma (,)</Option>
                  <Option value=";">Semicolon (;)</Option>
                  <Option value="\t">Tab (\t)</Option>
                  <Option value="|">Pipe (|)</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label="Has Header Row" name="hasHeader" initialValue={config.hasHeader !== false} valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Encoding" name="encoding" initialValue={config.encoding || 'utf-8'}>
            <Select>
              <Option value="utf-8">UTF-8</Option>
              <Option value="utf-16">UTF-16</Option>
              <Option value="ascii">ASCII</Option>
              <Option value="latin1">Latin-1</Option>
            </Select>
          </Form.Item>
        </>
      )}

      <div style={{ padding: '8px 12px', backgroundColor: '#f9f9f9', borderRadius: '4px', fontSize: '12px', marginTop: '12px' }}>
        <Text type="secondary">
          <strong>CSV Input Node:</strong><br/>
          • Uploads and analyzes local CSV files<br/>
          • Automatically detects column data types<br/>
          • Provides data schema for downstream nodes<br/>
          • Supports custom delimiters and encoding<br/>
          • Shows preview of first 3 rows for validation
        </Text>
      </div>
    </div>
  );
};

export default CsvInputNodeProperties;